import React, { useMemo, useState } from 'react';
import { Box, Collapse, Stack, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

interface GroupedHistoryListProps<T> {
  items: T[];
  /** Items are grouped under this (preserving first-seen order, so already-sorted lists stay sorted). */
  groupKey: (item: T) => string;
  itemKey: (item: T) => string;
  renderItem: (item: T) => React.ReactNode;
  /** Items shown per group before a "Show more" link; also the increment each click adds. */
  pageSize?: number;
}

/** Previous-simulations/previous-scans style list: grouped by track, each group collapsible and paginated. */
export function GroupedHistoryList<T>({ items, groupKey, itemKey, renderItem, pageSize = 5 }: GroupedHistoryListProps<T>) {
  const groups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, T[]>();
    for (const item of items) {
      const key = groupKey(item);
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key)!.push(item);
    }
    return order.map((key) => ({ key, items: map.get(key)! }));
  }, [items, groupKey]);

  // The newest (first) group opens by default so a user with one track dominating their history isn't stuck
  // clicking through tabs to see anything; everything else starts collapsed.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(groups.length > 0 ? [groups[0].key] : []));
  const [shown, setShown] = useState<Record<string, number>>({});

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <Stack spacing={0.5}>
      {groups.map(({ key, items: groupItems }) => {
        const open = openGroups.has(key);
        const n = shown[key] ?? pageSize;
        const visible = groupItems.slice(0, n);
        const hasMore = groupItems.length > n;
        return (
          <Box key={key}>
            <Box
              onClick={() => toggleGroup(key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                py: 0.75,
                px: 0.5,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {key}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {groupItems.length}
                </Typography>
                {open ? <ExpandLess fontSize="small" color="action" /> : <ExpandMore fontSize="small" color="action" />}
              </Stack>
            </Box>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Stack spacing={1} sx={{ pl: 0.5, pb: 1, pt: 0.5 }}>
                {visible.map((item) => (
                  <React.Fragment key={itemKey(item)}>{renderItem(item)}</React.Fragment>
                ))}
                {hasMore && (
                  <Box
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShown((prev) => ({ ...prev, [key]: n + pageSize }));
                    }}
                    sx={{
                      border: 0,
                      bgcolor: 'transparent',
                      color: 'primary.main',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      textAlign: 'left',
                      py: 0.5,
                      px: 0.5,
                    }}
                  >
                    Show {Math.min(pageSize, groupItems.length - n)} more
                  </Box>
                )}
              </Stack>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
}
